import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Pencil, Calendar, Tag, Save, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { Card } from '@/shared/components/ui/Card';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useLeaveTypes,
  useLeaveTypeMutations,
  useHolidays,
  useHolidayMutations,
} from '../hooks/useHr';
import { fmtDate } from '../constants';
import type { LeaveTypeConfig, CreateLeaveTypePayload } from '../types';

type Tab = 'types' | 'holidays';

export function LeaveSettings() {
  const [tab, setTab] = useState<Tab>('types');

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center gap-3">
        <Tag className="h-5 w-5 text-primary" />
        <h2 className="text-display-sm font-semibold text-on-surface">Leave Policy Settings</h2>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 rounded bg-surface-variant/50 p-1">
        <button
          type="button"
          onClick={() => setTab('types')}
          className={`rounded px-4 py-1.5 text-body-sm font-medium transition-colors ${
            tab === 'types' ? 'bg-surface text-on-surface shadow-surface-1' : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Leave Types
        </button>
        <button
          type="button"
          onClick={() => setTab('holidays')}
          className={`rounded px-4 py-1.5 text-body-sm font-medium transition-colors ${
            tab === 'holidays' ? 'bg-surface text-on-surface shadow-surface-1' : 'text-on-surface-variant hover:text-on-surface'
          }`}
        >
          Holiday Calendar
        </button>
      </div>

      {tab === 'types' ? <LeaveTypesTab /> : <HolidaysTab />}
    </div>
  );
}

// ─── Leave Types Tab ──────────────────────────────────────────────────────────

function LeaveTypesTab() {
  const { data: leaveTypes = [], isLoading } = useLeaveTypes(true);
  const { createLeaveType, updateLeaveType, deleteLeaveType } = useLeaveTypeMutations();
  const [showCreate, setShowCreate] = useState(false);
  const [editCode, setEditCode] = useState<string | null>(null);

  if (isLoading) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-body-sm text-on-surface-variant">
          {leaveTypes.length} leave type{leaveTypes.length !== 1 ? 's' : ''} configured
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> Add Type
        </Button>
      </div>

      {showCreate && (
        <LeaveTypeForm
          onSubmit={(payload) => {
            createLeaveType.mutate(payload, {
              onSuccess: () => { toast.success('Leave type created.'); setShowCreate(false); },
              onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to create leave type.')),
            });
          }}
          onCancel={() => setShowCreate(false)}
          loading={createLeaveType.isPending}
        />
      )}

      {leaveTypes.length === 0 && !showCreate && (
        <EmptyState title="No leave types" description="Create your first leave type to get started." />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {leaveTypes.map((lt) =>
          editCode === lt.code ? (
            <LeaveTypeForm
              key={lt.code}
              initial={lt}
              onSubmit={(payload) => {
                updateLeaveType.mutate(
                  { code: lt.code, payload },
                  {
                    onSuccess: () => { toast.success('Leave type updated.'); setEditCode(null); },
                    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to update.')),
                  },
                );
              }}
              onCancel={() => setEditCode(null)}
              loading={updateLeaveType.isPending}
              isEdit
            />
          ) : (
            <LeaveTypeCard
              key={lt.code}
              lt={lt}
              onEdit={() => setEditCode(lt.code)}
              onDelete={() => {
                if (!confirm(`Deactivate "${lt.name}"? Employees won't be able to select it.`)) return;
                deleteLeaveType.mutate(lt.code, {
                  onSuccess: () => toast.success('Leave type deactivated.'),
                  onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to deactivate.')),
                });
              }}
            />
          ),
        )}
      </div>
    </div>
  );
}

function LeaveTypeCard({ lt, onEdit, onDelete }: { lt: LeaveTypeConfig; onEdit: () => void; onDelete: () => void }) {
  return (
    <Card className="relative overflow-hidden px-4 py-3">
      <div className="absolute left-0 top-0 h-full w-1.5" style={{ backgroundColor: lt.color }} />
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-on-surface">{lt.name}</p>
          <p className="text-[10px] uppercase text-on-surface-variant">{lt.code}</p>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={onEdit} className="rounded p-1 hover:bg-surface-variant" aria-label="Edit">
            <Pencil className="h-3.5 w-3.5 text-on-surface-variant" />
          </button>
          {lt.is_active && (
            <button type="button" onClick={onDelete} className="rounded p-1 hover:bg-surface-variant" aria-label="Deactivate">
              <Trash2 className="h-3.5 w-3.5 text-danger-text" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge tone={lt.is_paid ? 'success' : 'neutral'}>{lt.is_paid ? 'Paid' : 'Unpaid'}</Badge>
        <Badge tone="info">{lt.default_annual_quota}d/yr</Badge>
        {!lt.is_active && <Badge tone="danger">Inactive</Badge>}
        {lt.requires_document && <Badge tone="warning">Doc Required</Badge>}
      </div>
    </Card>
  );
}

function LeaveTypeForm({
  initial,
  onSubmit,
  onCancel,
  loading,
  isEdit,
}: {
  initial?: LeaveTypeConfig;
  onSubmit: (payload: CreateLeaveTypePayload) => void;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [isPaid, setIsPaid] = useState(initial?.is_paid ?? true);
  const [quota, setQuota] = useState(String(initial?.default_annual_quota ?? 0));
  const [requiresDoc, setRequiresDoc] = useState(initial?.requires_document ?? false);
  const [docThreshold, setDocThreshold] = useState(String(initial?.document_threshold_days ?? 3));
  const [color, setColor] = useState(initial?.color ?? '#3b82f6');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      code: code.toLowerCase().trim(),
      name: name.trim(),
      is_paid: isPaid,
      default_annual_quota: Number(quota) || 0,
      requires_document: requiresDoc,
      document_threshold_days: Number(docThreshold) || 0,
      color,
    });
  };

  return (
    <Card className="px-4 py-3">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Code"
            name="lt-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. casual"
            disabled={isEdit}
            required
          />
          <Input
            label="Name"
            name="lt-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Casual Leave"
            required
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-on-surface">Paid?</label>
            <Select
              name="lt-paid"
              value={isPaid ? 'yes' : 'no'}
              onChange={(e) => setIsPaid(e.target.value === 'yes')}
              options={[{ value: 'yes', label: 'Paid' }, { value: 'no', label: 'Unpaid' }]}
            />
          </div>
          <Input
            label="Annual Quota"
            name="lt-quota"
            type="number"
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
            min="0"
          />
          <div>
            <label className="mb-1 block text-[11px] font-medium text-on-surface">Color</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-full cursor-pointer rounded border border-outline"
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={requiresDoc}
              onChange={(e) => setRequiresDoc(e.target.checked)}
              className="h-4 w-4 rounded border border-outline bg-white text-primary accent-primary focus:ring-2 focus:ring-primary/40"
            />
            <span className="text-body-sm text-on-surface">Requires document</span>
          </label>
          {requiresDoc && (
            <Input
              label=""
              name="lt-doc-threshold"
              type="number"
              value={docThreshold}
              onChange={(e) => setDocThreshold(e.target.value)}
              min="1"
              placeholder="Days threshold"
              className="w-24"
            />
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button type="submit" size="sm" loading={loading}>
            <Save className="h-3.5 w-3.5" /> {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ─── Holidays Tab ─────────────────────────────────────────────────────────────

interface HolidayRow {
  id: string;
  date: string;
  name: string;
  is_optional: boolean;
}

let rowIdCounter = 0;
function newRow(): HolidayRow {
  return { id: `row-${++rowIdCounter}`, date: '', name: '', is_optional: false };
}

function HolidaysTab() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data: holidays = [], isLoading } = useHolidays(year);
  const { createHoliday, deleteHoliday } = useHolidayMutations();
  const [rows, setRows] = useState<HolidayRow[]>([]);
  const [saving, setSaving] = useState(false);

  const yearOptions = useMemo(() => {
    return [currentYear - 1, currentYear, currentYear + 1].map((y) => ({
      value: String(y),
      label: String(y),
    }));
  }, [currentYear]);

  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const updateRow = (id: string, field: keyof HolidayRow, value: string | boolean) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSaveAll = async () => {
    const valid = rows.filter((r) => r.date && r.name.trim());
    if (valid.length === 0) return;
    setSaving(true);
    let success = 0;
    for (const row of valid) {
      try {
        await createHoliday.mutateAsync({ date: row.date, name: row.name.trim(), is_optional: row.is_optional });
        success++;
      } catch {
        // skip duplicates silently
      }
    }
    setSaving(false);
    if (success > 0) {
      toast.success(`${success} holiday${success > 1 ? 's' : ''} added.`);
      setRows([]);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select
            name="holiday-year"
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            options={yearOptions}
          />
          <p className="text-body-sm text-on-surface-variant">
            {holidays.length} holiday{holidays.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button size="sm" onClick={addRow}>
          <Plus className="h-4 w-4" /> Add Holiday
        </Button>
      </div>

      {/* Multi-row add form */}
      {rows.length > 0 && (
        <Card className="px-4 py-3">
          <div className="flex flex-col gap-2">
            {/* Header row */}
            <div className="grid grid-cols-[140px_1fr_80px_32px] items-center gap-3 text-[11px] font-medium uppercase text-on-surface-variant">
              <span>Date</span>
              <span>Name</span>
              <span>Optional</span>
              <span />
            </div>
            {/* Data rows */}
            {rows.map((row) => (
              <div key={row.id} className="grid grid-cols-[140px_1fr_80px_32px] items-center gap-3">
                <div className="relative">
                  <input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateRow(row.id, 'date', e.target.value)}
                    className="h-9 w-full rounded border border-outline bg-white px-2 pr-8 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <Calendar className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
                </div>
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => updateRow(row.id, 'name', e.target.value)}
                  placeholder="e.g. Republic Day"
                  className="h-9 rounded border border-outline bg-white px-3 text-data-md text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <label className="flex cursor-pointer items-center justify-center">
                  <input
                    type="checkbox"
                    checked={row.is_optional}
                    onChange={(e) => updateRow(row.id, 'is_optional', e.target.checked)}
                    className="h-4 w-4 cursor-pointer appearance-none rounded-none border border-black bg-white checked:border-black checked:bg-primary focus:ring-2 focus:ring-primary/40"
                    style={{ backgroundImage: row.is_optional ? "url(\"data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")" : undefined, backgroundSize: '100% 100%', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="flex h-8 w-8 items-center justify-center rounded hover:bg-surface-variant"
                  aria-label="Remove row"
                >
                  <X className="h-3.5 w-3.5 text-danger-text" />
                </button>
              </div>
            ))}
          </div>
          {/* Footer actions */}
          <div className="mt-3 flex items-center justify-between">
            <Button type="button" variant="ghost" size="sm" onClick={addRow}>
              <Plus className="h-3.5 w-3.5" /> Add Another Row
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setRows([])}>
                Cancel
              </Button>
              <Button
                size="sm"
                loading={saving}
                onClick={handleSaveAll}
                disabled={rows.every((r) => !r.date || !r.name.trim())}
              >
                <Save className="h-3.5 w-3.5" /> Save {rows.filter((r) => r.date && r.name.trim()).length} Holiday{rows.filter((r) => r.date && r.name.trim()).length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {holidays.length === 0 && rows.length === 0 && (
        <EmptyState title="No holidays" description={`No holidays configured for ${year}.`} />
      )}

      <div className="flex flex-col gap-2">
        {holidays.map((h) => (
          <Card key={h.id} className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-primary" />
              <div>
                <p className="font-medium text-on-surface">{h.name}</p>
                <p className="text-[11px] text-on-surface-variant">{fmtDate(h.date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {h.is_optional && <Badge tone="info">Optional</Badge>}
              <button
                type="button"
                onClick={() => {
                  if (!confirm(`Remove "${h.name}" from the holiday calendar?`)) return;
                  deleteHoliday.mutate(h.id, {
                    onSuccess: () => toast.success('Holiday removed.'),
                    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to remove holiday.')),
                  });
                }}
                className="rounded p-1 hover:bg-surface-variant"
                aria-label="Remove"
              >
                <Trash2 className="h-3.5 w-3.5 text-danger-text" />
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
